/**
 * Vite-time environment access + Pi base-URL resolution.
 *
 * Vite exposes `import.meta.env` for variables prefixed with `VITE_`:
 *
 *   - VITE_REEFAPP_DEV           : '1' to enable the /dev panel
 *   - VITE_REEFAPP_AGENT_DEV_URL : override the agent origin (dev)
 *   - VITE_REEFAPP_PROXY_MODE    : '1' to force same-origin (Vite proxy)
 *   - VITE_REEFAPP_PI_BASE_URL   : the Pi's public base URL — set ONLY on
 *     the Vercel build (https://reefapp-pi.tail7e0fe8.ts.net). The
 *     Pi-served build omits it so the kiosk and LAN access stay
 *     same-origin.
 *
 * Base-URL resolution order for API calls (`agentDevUrl`):
 *   1. localStorage `reefapp.piBaseUrl` — user override from Settings.
 *   2. VITE_REEFAPP_PROXY_MODE=1 → '' (same-origin through Vite's proxy).
 *   3. VITE_REEFAPP_AGENT_DEV_URL — explicit dev override.
 *   4. VITE_REEFAPP_PI_BASE_URL — Vercel build → the Tailscale Pi URL.
 *   5. Same-origin when served over HTTPS (nginx proxies /api etc.);
 *      `http://<host>:3002` on plain-HTTP dev.
 */
/** The agent's dev HTTP port. The agent always binds this port. */
const AGENT_DEV_PORT = 3002;

/** localStorage key for the user-set Pi base URL override (Settings). */
export const PI_BASE_URL_STORAGE_KEY = 'reefapp.piBaseUrl';

export interface PublicEnv {
  devPanelEnabled: boolean;
  /** Resolved agent/Pi API origin, '' = same-origin (no trailing slash). */
  agentDevUrl: string;
}

/** Read the Settings override; null when unset/blank/unavailable. */
export function readPiBaseUrlOverride(): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const v = window.localStorage.getItem(PI_BASE_URL_STORAGE_KEY);
    if (!v) return null;
    const trimmed = v.trim().replace(/\/+$/, '');
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

/** Persist (or clear, with null/blank) the Settings override. */
export function writePiBaseUrlOverride(value: string | null): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const trimmed = value?.trim().replace(/\/+$/, '') ?? '';
    if (trimmed.length === 0) {
      window.localStorage.removeItem(PI_BASE_URL_STORAGE_KEY);
    } else {
      window.localStorage.setItem(PI_BASE_URL_STORAGE_KEY, trimmed);
    }
  } catch {
    // Storage unavailable — override simply won't stick.
  }
}

/**
 * Resolve the default agent endpoint when nothing else applies.
 *
 * Resolves to the SAME host the web app was served from: over HTTPS
 * we're behind the Pi's nginx reverse proxy → same-origin (''); over
 * plain HTTP (LAN dev) → `http://<host>:3002`. In a non-browser context
 * (unit tests / SSR) falls back to loopback.
 */
function resolveDefaultAgentDevUrl(): string {
  if (
    typeof window !== 'undefined' &&
    typeof window.location !== 'undefined' &&
    typeof window.location.hostname === 'string' &&
    window.location.hostname.length > 0
  ) {
    if (window.location.protocol === 'https:') {
      return '';
    }
    return `http://${window.location.hostname}:${AGENT_DEV_PORT}`;
  }
  return `http://127.0.0.1:${AGENT_DEV_PORT}`;
}

function readViteVar(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value.replace(/\/+$/, '') : null;
}

export function loadPublicEnv(): PublicEnv {
  const env = import.meta.env;
  const override = readPiBaseUrlOverride();
  const proxyMode = env.VITE_REEFAPP_PROXY_MODE === '1';
  const agentDevUrl =
    override ??
    (proxyMode
      ? ''
      : (readViteVar(env.VITE_REEFAPP_AGENT_DEV_URL) ??
        readViteVar(env.VITE_REEFAPP_PI_BASE_URL) ??
        resolveDefaultAgentDevUrl()));

  return {
    devPanelEnabled: env.VITE_REEFAPP_DEV === '1',
    agentDevUrl,
  };
}

/**
 * Base URL for Pi-hosted STATIC assets (TOTM archive, coral photos).
 * These are served by the Pi's nginx, not the agent, so the `:3002`
 * plain-HTTP fallback never applies: user override → Vercel's
 * VITE_REEFAPP_PI_BASE_URL → same-origin relative path.
 */
export function piAssetUrl(path: string): string {
  const env = import.meta.env;
  const base = readPiBaseUrlOverride() ?? readViteVar(env.VITE_REEFAPP_PI_BASE_URL) ?? '';
  return `${base}${path}`;
}
